import { useEffect, useRef } from "react";
import { LngLatBounds, Map as MapLibreMap, Marker, NavigationControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { STATE_META, vehicleState } from "@/lib/fleet";

const STYLE = {
  version: 8,
  sources: {
    basemap: {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution: "© OpenStreetMap, © CARTO",
    },
    terrain: {
      type: "raster-dem",
      tiles: ["https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png"],
      encoding: "terrarium",
      tileSize: 256,
      maxzoom: 13,
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#0A0A0A" } },
    { id: "basemap", type: "raster", source: "basemap" },
  ],
  sky: { "sky-color": "#0A0A0A", "horizon-color": "#123", "fog-color": "#0A0A0A", "fog-ground-blend": 0.6 },
};

const markerEl = (vehicle, selected) => {
  const meta = STATE_META[vehicleState(vehicle)];
  const el = document.createElement("div");
  el.className = `doot-marker ${meta.cls} ${selected ? "selected" : ""}`;
  el.innerHTML = `${meta.cls === "moving" ? '<div class="ring"></div>' : ""}${
    selected ? '<div class="halo"></div>' : ""
  }<div class="core" style="background:${meta.color}"></div><div class="plate">${vehicle.plate_number}</div>`;
  return el;
};

export const Fleet3DMap = ({ vehicles, selectedId, onSelect, geofences = [] }) => {
  const holder = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef({});
  const fitted = useRef(false);

  useEffect(() => {
    if (mapRef.current) return;
    const map = new MapLibreMap({
      container: holder.current,
      style: STYLE,
      center: [93.7, 25.9],
      zoom: 5.5,
      pitch: 55,
      bearing: -18,
      attributionControl: false,
    });
    map.addControl(new NavigationControl({ visualizePitch: true }), "top-left");
    map.on("load", () => {
      map.setTerrain({ source: "terrain", exaggeration: 1.4 });
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markers.current = {};
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set();
    vehicles.forEach((v) => {
      if (!v.current_location) return;
      seen.add(v.id);
      const pos = [v.current_location.lng, v.current_location.lat];
      const existing = markers.current[v.id];
      if (existing) {
        existing.setLngLat(pos);
        existing.getElement().replaceWith(markerEl(v, v.id === selectedId));
        existing._element = existing.getElement();
      } else {
        const el = markerEl(v, v.id === selectedId);
        el.addEventListener("click", () => onSelect(v.id));
        markers.current[v.id] = new Marker({ element: el }).setLngLat(pos).addTo(map);
      }
    });
    Object.keys(markers.current).forEach((id) => {
      if (!seen.has(id)) {
        markers.current[id].remove();
        delete markers.current[id];
      }
    });

    if (!fitted.current && seen.size > 0) {
      const bounds = new LngLatBounds();
      vehicles
        .filter((v) => v.current_location)
        .forEach((v) => bounds.extend([v.current_location.lng, v.current_location.lat]));
      map.fitBounds(bounds, { padding: 120, maxZoom: 11, pitch: 55, duration: 900 });
      fitted.current = true;
    }
  }, [vehicles, selectedId, onSelect]);

  // recentre whenever a truck is selected or its position updates
  const selected = vehicles.find((v) => v.id === selectedId);
  const lat = selected?.current_location?.lat;
  const lng = selected?.current_location?.lng;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || lat === undefined) return;
    map.easeTo({
      center: [lng, lat],
      zoom: Math.max(map.getZoom(), 12.5),
      pitch: 60,
      duration: 900,
    });
  }, [lat, lng]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const draw = () => {
      const data = {
        type: "FeatureCollection",
        features: geofences
          .filter((f) => f.is_active)
          .map((f) => ({
            type: "Feature",
            properties: { name: f.name },
            geometry: { type: "Point", coordinates: [f.center.lng, f.center.lat] },
            radius_m: f.radius_m,
          })),
      };
      if (map.getSource("fences")) {
        map.getSource("fences").setData(data);
        return;
      }
      map.addSource("fences", { type: "geojson", data });
      map.addLayer({
        id: "fence-fill",
        type: "circle",
        source: "fences",
        paint: {
          "circle-radius": 26,
          "circle-color": "#00F0FF",
          "circle-opacity": 0.12,
          "circle-stroke-color": "#00F0FF",
          "circle-stroke-width": 1,
        },
      });
    };
    if (map.isStyleLoaded()) draw();
    else map.once("load", draw);
  }, [geofences]);

  return <div ref={holder} data-testid="fleet-3d-map" className="h-full w-full" />;
};
